package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.NotificationCategory
import com.evoapps.evoshift.domain.model.NotificationSettings
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.repository.NotificationSettingsRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_save_failed
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class NotificationSettingsUiState(
    /** Only until the first settings arrive (then they stay on screen). */
    val isLoading: Boolean = true,
    /** The switches this person sees: managers' kinds only for managers. */
    val categories: List<NotificationCategory> = emptyList(),
    val settings: NotificationSettings = NotificationSettings(),
    /** SH-241: owners see the switch for the trial e-mails. */
    val showsTrialEmails: Boolean = false,
    val error: UiText? = null
)

/**
 * The notification settings screen (SH-73, from the profile): one push switch per kind of
 * notification. A switch flips at once and is saved behind it; a failed save flips it back.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class NotificationSettingsViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val repository: NotificationSettingsRepository
) : ViewModel() {

    private val _uiState = MutableStateFlow(NotificationSettingsUiState())
    val uiState: StateFlow<NotificationSettingsUiState> = _uiState.asStateFlow()

    init {
        viewModelScope.launch {
            getCurrentUserUseCase()
                .flatMapLatest { user ->
                    if (user == null) flowOf(null)
                    else repository.observe(user.uid).map { user to it }
                }
                .catch { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } } // SH-188
                .collect { loaded ->
                    _uiState.update {
                        if (loaded == null) NotificationSettingsUiState(isLoading = false)
                        else it.copy(
                            isLoading = false,
                            categories = categoriesFor(loaded.first),
                            settings = loaded.second,
                            showsTrialEmails = owns(loaded.first)
                        )
                    }
                }
        }
    }

    fun setPush(category: NotificationCategory, on: Boolean) {
        save({ it.with(category, on) }, rollBack = { now, before -> now.with(category, before.isOn(category)) })
    }

    /** SH-241: the trial e-mails on or off. */
    fun setTrialEmails(on: Boolean) {
        save({ it.copy(trialEmails = on) }, rollBack = { now, before -> now.copy(trialEmails = before.trialEmails) })
    }

    /** Flips at once, saves behind it; a failed save undoes only this change. */
    private fun save(change: (NotificationSettings) -> NotificationSettings, rollBack: (NotificationSettings, NotificationSettings) -> NotificationSettings) {
        val before = _uiState.value.settings
        val after = change(before)
        if (after == before) return
        _uiState.update { it.copy(settings = after) }
        viewModelScope.launch {
            val user = getCurrentUserUseCase().first() ?: return@launch
            repository.save(user.uid, after).onFailure { e ->
                _uiState.update {
                    it.copy(
                        settings = rollBack(it.settings, before),
                        error = UiText.Resource(Res.string.error_save_failed, e.toUiText())
                    )
                }
            }
        }
    }

    fun messageShown() = _uiState.update { it.copy(error = null) }

    private fun owns(user: User): Boolean =
        user.engagements.values.any { it.isActive && it.permission == UserPermission.OWNER }

    private fun categoriesFor(user: User): List<NotificationCategory> {
        val manages = user.engagements.values.any { it.isActive && it.permission != UserPermission.EMPLOYEE }
        return NotificationCategory.entries.filter { manages || !it.managersOnly }
    }
}
